/// <amd-module name="Build/Designer/NewYamlTab" />
import { CodeLensOptions } from "Build/Common/Library/Contracts";
import { EditorConstants } from "Build/Designer/Constants";
import { focusYamlAssistantButton, NewYamlEditorHeader } from "Build/Designer/NewYamlEditorHeader";
import { getPipelineEditorViewService, IPipelineEditorViewService } from "Build/Designer/Services/PipelineEditorViewService";
import { YamlEditor } from "Build/Designer/YamlEditor";
import { YamlSidePanel } from "Build/Designer/YamlSidePanel";
import * as React from "react";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";


interface NewYamlTabProps {
    /* Subheader props */
    onBranchPickerError?: (errorMessage: string) => void;
    onFileNameChanged?: (fileName: string) => void;

    /* Editor props */
    content: string;
    editor: monaco.editor.ICodeEditor;
    onContentUpdated: (newContent: string) => void;
    onEditorCreated: (editor: monaco.editor.IEditor) => void;
    onEditorDisposing?: (editor: monaco.editor.IEditor) => void;
    codeLensOptions?: CodeLensOptions;

    /* Side panel props */
    sidePanelInitiallyOpen: boolean;
}

interface NewYamlTabState {
    sidePanelIsOpen: boolean;
}

/**
 * YamlTab component includes the YAML editor, subheader, and task assistant pane.
 */
export class NewYamlTab extends VssComponent<NewYamlTabProps, NewYamlTabState, IVssComponentContext> {
    private viewService: IPipelineEditorViewService;

    constructor(props: NewYamlTabProps, context: IVssComponentContext) {
        super(props, context);
        this.state = { sidePanelIsOpen: props.sidePanelInitiallyOpen };
        this.viewService = getPipelineEditorViewService(context.pageContext);
    }

    public render() {
        const props = this.props;

        return (
            <div className="ci-yaml-editor-tab flex-grow flex-row">
                <div className={"flex-row absolute-fill"}>
                    <div className="flex-column flex-grow relative">
                        <div className="ci-yaml-header-container relative">
                            <NewYamlEditorHeader
                                sidePanelIsOpen={this.state.sidePanelIsOpen}
                                onBranchPickerError={props.onBranchPickerError}
                                onSidePanelButtonClick={this._onSidePanelButtonClick}
                                onFileNameChanged={this.props.onFileNameChanged}
                            />
                        </div>
                        <div className="relative flex-grow">
                            <div className="absolute-fill">
                                <YamlEditor
                                    content={props.content}
                                    spinnerMessage={this.viewService.getSpinnerMessage()}
                                    errorMessage=""
                                    isReadOnly={false}
                                    elementToFocusOnEscape={"." + EditorConstants.ElementToFocusOnEscape}
                                    onEditorContentUpdated={this._onEditorContentUpdated}
                                    onEditorCreated={props.onEditorCreated}
                                    onEditorDisposing={props.onEditorDisposing}
                                    sidePanelIsOpen={this.state.sidePanelIsOpen}
                                    codeLensOptions={{onEditTask: this._openSidePanel, onOpenTemplate: props.codeLensOptions?.onOpenTemplate, onValidate: props.codeLensOptions?.onValidate, checkTemplate: props.codeLensOptions?.checkTemplate}}
                                />
                            </div>
                        </div>
                    </div>
                    <YamlSidePanel editor={props.editor} isOpen={this.state.sidePanelIsOpen} onDismiss={this._onSidePanelButtonClick} />
                </div>
            </div>
        );
    }

    private _onEditorContentUpdated = (editor: any /*IEditor*/) => {
        if (editor && this.props.onContentUpdated) {
            const content = editor.getValue() || "";
            this.props.onContentUpdated(content);
        }
    };

    private _onSidePanelButtonClick = () => {
        this.setState(
            prevState => ({
                sidePanelIsOpen: !prevState.sidePanelIsOpen
            }),
            () => {
                // Since the YAML Assistant toggle button moves between the sub-header (Show) and panel (Hide),
                // we shift the focus to the newly visible element on toggle to provide the illusion of consistent keyboard focus.
                focusYamlAssistantButton();
            }
        );
    };

    private _openSidePanel = () => {
        if (!this.state.sidePanelIsOpen) {
            this.setState({
                sidePanelIsOpen: true
            });
        }
    };
}
